import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../seo/SEO'
import InfoPage from './InfoPage'

const HowItWorks: React.FC = () => (
  <>
    <SEO
      title="How MakeMyDay Works | Your Daily Message, Action and Community"
      description="A step-by-step look at MakeMyDay: choose your time, get a daily message and a small action, build a streak, and share kind notes with a moderated community."
    />
    <InfoPage
      subtitle="How It Works"
      title="A Good Moment, Every Day"
      lead="MakeMyDay is built around a short daily rhythm: one message, one small action, and a community of people sharing kind words. Here is how each part works."
    >
      <ol className="info-steps">
        <li>
          <h3>Choose when your day should start</h3>
          <p>
            When you sign up, you pick the hour you would like to hear from MakeMyDay. We use your
            time zone, so the message arrives at that hour wherever you are, and you can change it
            at any time. If you allow notifications, a gentle push tells you the day's message is
            ready. If you would rather not have notifications, everything is waiting for you when
            you open the app.
          </p>
        </li>
        <li>
          <h3>Read your daily message</h3>
          <p>
            Each day brings one short, uplifting message. It might offer a new way to look at a
            hard morning, a reminder to be patient with yourself, or simply something that makes
            you smile. Missed a day? Recent messages stay available, so you can catch up on the
            ones you missed.
          </p>
        </li>
        <li>
          <h3>Do one small action</h3>
          <p>
            Alongside the message comes a simple action that takes a few minutes at most: note one
            thing you are grateful for, take a short walk without your phone, drink a glass of
            water slowly, or send a kind message to someone you have not spoken to in a while.
            When you have done it, mark it as done. Completing actions on consecutive days builds
            a <strong>streak</strong>, a quiet way to see a good habit taking shape.
          </p>
        </li>
        <li>
          <h3>Share a note with the community</h3>
          <p>
            The community feed is where people share short positive notes of their own: a thought
            that helped them, a small win, or a kind word for whoever reads it next. Free accounts
            can share one community note a day, plus one more after an optional short video.
            Notes are written in English and can't contain links, email addresses or phone
            numbers, which keeps the feed personal and free of spam. You can edit or delete your
            own notes whenever you like.
          </p>
        </li>
        <li>
          <h3>Like, save and follow</h3>
          <p>
            Tap the heart on a note that made your day, or save it to come back to later. When
            someone's notes keep resonating with you, follow them: their profile shows what they
            have shared, and the <strong>Following</strong> feed gathers notes from everyone you
            follow in one place.
          </p>
        </li>
      </ol>

      <h2>How we keep the community kind</h2>
      <p>
        A positive space needs looking after, so every note goes through several layers of
        protection before and after it appears:
      </p>
      <ul>
        <li>
          <strong>Automated checks</strong> run the moment a note is submitted, screening for
          offensive language, harmful content and spam before anything is published.
        </li>
        <li>
          <strong>Reporting</strong> lets anyone flag a note that feels wrong. Reported notes go
          to our team for review, and accounts that repeatedly break the rules can lose the ability
          to post.
        </li>
        <li>
          <strong>Blocking</strong> is always in your hands. If you block someone, neither of you
          sees the other's notes any more, any follows between you are removed, and nobody else
          is affected.
        </li>
        <li>
          <strong>Rate limits</strong> stop anyone from flooding the feed, so a few loud voices
          can't drown out everyone else.
        </li>
      </ul>

      <h2>Free and Plus</h2>
      <p>
        MakeMyDay is free to use. The daily message, the daily action, streaks, and reading,
        liking, saving and following in the community all cost nothing. The free version is
        supported by a small number of ads: an occasional sponsored card in a feed, and the
        optional video that unlocks an extra note. We keep ads deliberately sparse, and you are
        never required to watch one.
      </p>
      <p>
        <strong>MakeMyDay Plus</strong> is a planned paid option that removes ads and the daily
        limit on community notes. It is not available to buy yet; when it is, we will explain
        pricing and cancellation clearly before anyone subscribes.
      </p>

      <h2>Where you can use MakeMyDay</h2>
      <p>
        The <a href="https://t.me/i_make_your_day_bot">MakeMyDay Telegram bot</a> is available
        today. It sends your daily message at the hour you choose and can also suggest a gratitude
        prompt, a self-care tip or a feel-good song whenever you ask. Our Android and iOS apps,
        which bring the full experience described on this page, are being prepared for release,
        along with a web version.
      </p>

      <h2>Your account, your choice</h2>
      <p>
        You decide what MakeMyDay knows about you. An email address is optional, notifications
        are optional, and you can delete your account from inside the app at any time. Deletion
        has a 30-day grace period in case you change your mind; after that, your account and data
        are removed for good. The <Link to="/privacy">Privacy Policy</Link> covers the details,
        and the <Link to="/faq">FAQ</Link> answers the questions we hear most often.
      </p>
    </InfoPage>
  </>
)

export default HowItWorks
